import { supabase } from '../client';
import type { Requisition, RequisitionInsert, RequisitionStatus } from '@core/types';

export type RequisitionSortColumn = 'title' | 'created_at' | 'status' | 'target_close_date';

export interface RequisitionListOptions {
  clientId?: string;
  status?: RequisitionStatus;
  search?: string;
  page?: number;
  pageSize?: number;
  sortBy?: RequisitionSortColumn;
  sortAsc?: boolean;
}

export interface RequisitionListResult {
  data: Requisition[];
  total: number;
}

export const requisitionService = {
  async list(orgId: string, filters?: RequisitionListOptions): Promise<Requisition[] | RequisitionListResult> {
    const opts = filters ?? {};
    const sortCol: RequisitionSortColumn = opts.sortBy ?? 'created_at';
    const defaultAsc = sortCol === 'created_at' ? false : true;
    const ascending = opts.sortAsc ?? defaultAsc;

    let query = supabase
      .from('ggj_b2b_requisitions')
      .select(
        '*, client:ggj_b2b_clients(id, name), job:ggj_b2b_jobs(id, title, status), approver_user:ggj_b2b_org_users!approver_user_id(id, display_name, email)',
        {
          count: opts.pageSize ? 'exact' : undefined,
        }
      )
      .eq('org_id', orgId)
      .order(sortCol, { ascending, nullsFirst: false });

    if (opts.clientId) query = query.eq('client_id', opts.clientId);
    if (opts.status) query = query.eq('status', opts.status);
    if (opts.search) query = query.ilike('title', `%${opts.search}%`);

    if (opts.pageSize != null) {
      const page = Math.max(1, opts.page ?? 1);
      const from = (page - 1) * opts.pageSize;
      const to = from + opts.pageSize - 1;
      query = query.range(from, to);
    }

    const { data, error, count } = await query;
    if (error) throw error;

    if (opts.pageSize != null) {
      return { data: (data ?? []) as Requisition[], total: count ?? 0 };
    }
    return (data ?? []) as Requisition[];
  },

  async getById(id: string): Promise<Requisition | null> {
    const { data, error } = await supabase
      .from('ggj_b2b_requisitions')
      .select(
        '*, client:ggj_b2b_clients(id, name, contact_email), job:ggj_b2b_jobs(id, title, status, description)'
      )
      .eq('id', id)
      .single();
    if (error || !data) return null;
    const row = data as Requisition;
    const aid = row.approver_user_id;
    if (aid) {
      const { data: ou } = await supabase
        .from('ggj_b2b_org_users')
        .select('id, display_name, email')
        .eq('id', aid)
        .maybeSingle();
      if (ou) row.approver_user = ou;
    }
    return row;
  },

  async create(payload: RequisitionInsert): Promise<Requisition> {
    const { data, error } = await supabase.from('ggj_b2b_requisitions').insert(payload).select().single();
    if (error) throw error;
    return data as Requisition;
  },

  async update(id: string, payload: Partial<Requisition>): Promise<Requisition> {
    const { data, error } = await supabase
      .from('ggj_b2b_requisitions')
      .update(payload)
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return data as Requisition;
  },

  /** After creating a job from this requisition, link and move status toward active sourcing. */
  async linkJob(requisitionId: string, jobId: string): Promise<Requisition> {
    return this.update(requisitionId, {
      job_id: jobId,
      status: 'sourcing',
    } as Partial<Requisition>);
  },

  /** REQ-030: atomically approve + create a draft job via DB function */
  async approveAndCreateJob(
    requisitionId: string,
    approverId: string,
    notes?: string
  ): Promise<{ requisition_id: string; job_id: string }> {
    const { data, error } = await supabase.rpc('ggj_b2b_fn_approve_requisition', {
      p_requisition_id:   requisitionId,
      p_approver_user_id: approverId,
      p_notes:            notes ?? null,
    });
    if (error) throw error;
    const row = Array.isArray(data) ? data[0] : data;
    return row as { requisition_id: string; job_id: string };
  },

  async delete(id: string): Promise<void> {
    const { error } = await supabase.from('ggj_b2b_requisitions').delete().eq('id', id);
    if (error) throw error;
  },
};
